/*
 * Ambient fireflies — 22 of them drift across the world and wrap at edges.
 *
 * The day/night cycle (TASK-10) wants more fireflies at deep twilight and fewer
 * at dawn. We allocate the swarm at +50% above the baseline (33 flies) so we
 * have headroom, then per-frame dim the "extras" toward zero alpha based on a
 * count multiplier passed from game.ts. Spawning/despawning every frame would
 * cause Pixi to churn on the same Graphics, so this alpha approach keeps the
 * cost flat.
 *
 * Wind: during a drizzle event, the world state carries a small horizontal
 * drift the swarm picks up so the air feels moving.
 */

import { Container, Graphics } from 'pixi.js';
import type { Firefly } from '@/state/types';
import { WORLD } from '@/render/palette';

export interface FireflyView {
  container: Container;
  flies: Firefly[];
  /**
   * Per-frame update.
   * @param dt seconds since last frame
   * @param countFactor multiplier on the visible swarm size (default 1).
   *   1.0 = baseline ~22 flies visible; 1.4 = ~31; 0.6 = ~13. We cap the cap
   *   inside at +50% over baseline since the actual fly array is sized for that.
   * @param windX per-frame horizontal drift in world-units (default 0). Used
   *   during weather windows so fireflies lean with the rain.
   */
  update(dt: number, countFactor?: number, windX?: number): void;
}

/** Baseline fly count (matches v0.2). */
const FIREFLY_BASELINE = 22;

/** Cap the spawn pool at +50% over baseline per the TASK-10 spec. */
const FIREFLY_POOL = Math.ceil(FIREFLY_BASELINE * 1.5);

export function createFireflies(): FireflyView {
  const root = new Container();
  const g = new Graphics();
  root.addChild(g);

  const flies: Firefly[] = Array.from({ length: FIREFLY_POOL }, () => ({
    x: Math.random() * WORLD.width,
    y: 100 + Math.random() * (WORLD.height - 150),
    phase: Math.random() * Math.PI * 2,
    speed: 0.3 + Math.random() * 0.5,
    drift: Math.random() * 0.3,
  }));

  function update(dt: number, countFactor: number = 1, windX: number = 0): void {
    g.clear();
    // Translate countFactor → number of visible flies. Hard cap at the pool
    // size so a stray high factor can't index out-of-range.
    const want = Math.max(0, Math.min(FIREFLY_POOL, Math.round(FIREFLY_BASELINE * countFactor)));

    for (let i = 0; i < flies.length; i++) {
      const f = flies[i]!;
      f.phase += dt * f.speed * 2;
      f.x += Math.sin(f.phase * 0.6) * 0.4 + f.drift + windX;
      f.y += Math.cos(f.phase * 0.4) * 0.25;
      if (f.x < -5) f.x = WORLD.width;
      if (f.x > WORLD.width + 5) f.x = 0;
      if (f.y < 90) f.y = WORLD.height - 50;
      if (f.y > WORLD.height - 40) f.y = 90;

      // Visibility: the first `want` flies render at full pulse; everything
      // beyond that is invisible. We still simulate them so when the
      // multiplier rises they're already in motion (no popping in).
      if (i >= want) continue;

      const a = (Math.sin(f.phase) + 1) / 2;
      g.circle(f.x, f.y, 7).fill({ color: 0xfff3a0, alpha: a * 0.18 });
      g.circle(f.x, f.y, 1.6).fill({ color: 0xfff3a0, alpha: a * 0.95 });
    }
  }

  return { container: root, flies, update };
}
